// Safety and Health Protocol Service
// Handles safety protocols, concussion management, injury prevention, and emergency procedures

// Safety Protocol Categories
export const SAFETY_CATEGORIES = {
  CONCUSSION: 'concussion',
  INJURY_PREVENTION: 'injury_prevention',
  EMERGENCY: 'emergency',
  HEAT_ILLNESS: 'heat_illness',
  EQUIPMENT: 'equipment',
  GENERAL: 'general'
};

// Emergency Types
export const EMERGENCY_TYPES = {
  MEDICAL: 'medical',
  WEATHER: 'weather',
  FACILITY: 'facility',
  SECURITY: 'security'
};

// Injury Severity Levels
export const INJURY_SEVERITY = {
  MINOR: 'minor',
  MODERATE: 'moderate',
  SEVERE: 'severe',
  CRITICAL: 'critical'
};

// Mock Safety Protocols Data
const mockSafetyProtocols = [
  {
    id: 'concussion-protocol',
    title: 'Concussion Protocol',
    category: SAFETY_CATEGORIES.CONCUSSION,
    description: 'Comprehensive concussion management and return-to-play protocol',
    lastUpdated: '2024-01-15',
    priority: 'high',
    steps: [
      {
        step: 1,
        title: 'Immediate Recognition',
        description: 'Identify signs and symptoms of concussion',
        details: [
          'Remove athlete from play immediately',
          'Do not allow return to activity same day',
          'Assess using standardized concussion assessment tools',
          'Monitor for deteriorating symptoms'
        ]
      },
      {
        step: 2,
        title: 'Medical Evaluation',
        description: 'Professional medical assessment required',
        details: [
          'Refer to qualified healthcare professional',
          'Complete neurological examination',
          'Cognitive and balance testing',
          'Symptom inventory assessment'
        ]
      },
      {
        step: 3,
        title: 'Recovery Phase',
        description: 'Gradual return to activity protocol',
        details: [
          'Complete physical and cognitive rest initially',
          'Gradual increase in activity as tolerated',
          'Monitor symptoms throughout recovery',
          'Academic accommodations as needed'
        ]
      },
      {
        step: 4,
        title: 'Return-to-Play',
        description: 'Systematic progression back to full activity',
        details: [
          'Medical clearance required',
          'Graduated return-to-play protocol',
          'Minimum 24-hour intervals between stages',
          'Stop progression if symptoms return'
        ]
      }
    ],
    warningSigns: [
      'Loss of consciousness',
      'Severe or worsening headache',
      'Repeated vomiting',
      'Confusion or disorientation',
      'Seizures',
      'Difficulty staying awake'
    ],
    resources: [
      {
        title: 'CDC Concussion Guidelines',
        url: 'https://www.cdc.gov/traumatic-brain-injury/concussion/sports/index.html',
        type: 'external'
      },
      {
        title: 'NFHS Concussion Course',
        url: 'https://nfhslearn.com/courses/concussion-in-sports',
        type: 'external'
      }
    ]
  },
  {
    id: 'heat-illness-prevention',
    title: 'Heat Illness Prevention',
    category: SAFETY_CATEGORIES.HEAT_ILLNESS,
    description: 'Prevention and management of heat-related illnesses',
    lastUpdated: '2024-01-10',
    priority: 'high',
    steps: [
      {
        step: 1,
        title: 'Prevention Strategies',
        description: 'Proactive measures to prevent heat illness',
        details: [
          'Monitor weather conditions and heat index',
          'Ensure proper hydration before, during, and after activity',
          'Schedule activities during cooler parts of the day',
          'Provide frequent rest breaks in shade'
        ]
      },
      {
        step: 2,
        title: 'Recognition',
        description: 'Identify early signs of heat illness',
        details: [
          'Monitor for excessive fatigue or weakness',
          'Watch for changes in mental status',
          'Check for decreased performance',
          'Look for signs of dehydration'
        ]
      },
      {
        step: 3,
        title: 'Treatment',
        description: 'Immediate response to heat illness',
        details: [
          'Move to cool, shaded area immediately',
          'Remove excess clothing and equipment',
          'Apply cooling methods (ice, fans, cold water)',
          'Monitor vital signs and mental status'
        ]
      }
    ],
    warningSigns: [
      'High body temperature (>104°F)',
      'Altered mental state',
      'Hot, dry skin or profuse sweating',
      'Rapid pulse',
      'Nausea or vomiting',
      'Headache or dizziness'
    ]
  },
  {
    id: 'injury-prevention',
    title: 'Injury Prevention Program',
    category: SAFETY_CATEGORIES.INJURY_PREVENTION,
    description: 'Comprehensive injury prevention strategies for student-athletes',
    lastUpdated: '2024-01-12',
    priority: 'medium',
    steps: [
      {
        step: 1,
        title: 'Pre-Participation Screening',
        description: 'Comprehensive health and fitness assessment',
        details: [
          'Complete medical history and physical examination',
          'Cardiovascular screening',
          'Musculoskeletal assessment',
          'Previous injury evaluation'
        ]
      },
      {
        step: 2,
        title: 'Conditioning and Training',
        description: 'Sport-specific preparation and conditioning',
        details: [
          'Progressive training programs',
          'Strength and flexibility training',
          'Sport-specific skill development',
          'Proper technique instruction'
        ]
      },
      {
        step: 3,
        title: 'Equipment and Environment',
        description: 'Ensure safe equipment and playing conditions',
        details: [
          'Properly fitted protective equipment',
          'Regular equipment inspection and maintenance',
          'Safe playing surfaces and facilities',
          'Weather condition monitoring'
        ]
      }
    ]
  }
];

// Mock Emergency Procedures
const mockEmergencyProcedures = [
  {
    id: 'medical-emergency',
    title: 'Medical Emergency Response',
    type: EMERGENCY_TYPES.MEDICAL,
    description: 'Immediate response protocol for medical emergencies',
    priority: 'critical',
    steps: [
      'Ensure scene safety',
      'Assess responsiveness and breathing',
      'Call 911 immediately if needed',
      'Provide first aid/CPR as trained',
      'Contact parents/guardians',
      'Document incident thoroughly'
    ],
    contacts: [
      { role: 'Emergency Services', number: '911' },
      { role: 'Athletic Director', number: '(615) 555-0100' },
      { role: 'School Nurse', number: '(615) 555-0101' },
      { role: 'Principal', number: '(615) 555-0102' }
    ]
  },
  {
    id: 'severe-weather',
    title: 'Severe Weather Protocol',
    type: EMERGENCY_TYPES.WEATHER,
    description: 'Response procedures for severe weather conditions',
    priority: 'high',
    steps: [
      'Monitor weather conditions continuously',
      'Suspend outdoor activities when lightning detected',
      'Move all personnel to designated safe areas',
      'Wait 30 minutes after last lightning/thunder',
      'Resume activities only when conditions are safe',
      'Communicate with parents about delays/cancellations'
    ],
    lightningPolicy: {
      suspendDistance: '10 miles',
      waitTime: '30 minutes',
      safeAreas: ['Main building', 'Gymnasium', 'Locker rooms']
    }
  },
  {
    id: 'facility-emergency',
    title: 'Facility Emergency',
    type: EMERGENCY_TYPES.FACILITY,
    description: 'Response to facility-related emergencies',
    priority: 'high',
    steps: [
      'Evacuate affected area immediately',
      'Account for all personnel',
      'Contact emergency services if needed',
      'Notify facility management',
      'Document incident details',
      'Implement alternative plans if necessary'
    ]
  }
];

// Mock Injury Reports
const mockInjuryReports = [
  {
    id: 'injury-001',
    athleteName: 'John Smith',
    sport: 'Football',
    injuryType: 'Ankle Sprain',
    severity: INJURY_SEVERITY.MODERATE,
    dateOccurred: '2024-01-20',
    description: 'Rolled ankle during practice drill',
    treatment: 'Ice, elevation, medical evaluation',
    returnToPlay: 'Pending medical clearance',
    reportedBy: 'Coach Johnson'
  },
  {
    id: 'injury-002',
    athleteName: 'Sarah Davis',
    sport: 'Basketball',
    injuryType: 'Concussion',
    severity: INJURY_SEVERITY.SEVERE,
    dateOccurred: '2024-01-18',
    description: 'Head contact during game',
    treatment: 'Concussion protocol initiated',
    returnToPlay: 'Following graduated protocol',
    reportedBy: 'Athletic Trainer'
  }
];

// Mock Safety Resources
const mockSafetyResources = [
  {
    id: 'nfhs-courses',
    title: 'NFHS Safety Courses',
    category: 'education',
    description: 'National Federation of State High School Associations safety education',
    url: 'https://nfhslearn.com',
    required: true
  },
  {
    id: 'first-aid-certification',
    title: 'First Aid/CPR Certification',
    category: 'certification',
    description: 'Required certification for all coaches and staff',
    provider: 'American Red Cross',
    renewalPeriod: '2 years'
  },
  {
    id: 'emergency-contacts',
    title: 'Emergency Contact List',
    category: 'reference',
    description: 'Quick reference for emergency situations',
    contacts: [
      { service: 'Emergency Services', number: '911' },
      { service: 'Poison Control', number: '1-800-222-1222' },
      { service: 'Local Hospital', number: '(615) 555-1000' },
      { service: 'Athletic Director', number: '(615) 555-0100' }
    ]
  }
];

// Safety Service Class
class SafetyService {
  // Get all safety protocols
  static async getSafetyProtocols(filters = {}) {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 300));
    
    let protocols = [...mockSafetyProtocols];
    
    if (filters.category) {
      protocols = protocols.filter(protocol => protocol.category === filters.category);
    }
    
    if (filters.priority) {
      protocols = protocols.filter(protocol => protocol.priority === filters.priority);
    }
    
    return protocols;
  }
  
  // Get specific safety protocol
  static async getSafetyProtocol(id) {
    await new Promise(resolve => setTimeout(resolve, 200));
    return mockSafetyProtocols.find(protocol => protocol.id === id);
  }
  
  // Get emergency procedures
  static async getEmergencyProcedures(type = null) {
    await new Promise(resolve => setTimeout(resolve, 300));
    
    if (type) {
      return mockEmergencyProcedures.filter(procedure => procedure.type === type);
    }
    
    return mockEmergencyProcedures;
  }
  
  // Get specific emergency procedure
  static async getEmergencyProcedure(id) {
    await new Promise(resolve => setTimeout(resolve, 200));
    return mockEmergencyProcedures.find(procedure => procedure.id === id);
  }
  
  // Get injury reports
  static async getInjuryReports(filters = {}) {
    await new Promise(resolve => setTimeout(resolve, 300));
    
    let reports = [...mockInjuryReports];
    
    if (filters.sport) {
      reports = reports.filter(report => report.sport.toLowerCase() === filters.sport.toLowerCase());
    }
    
    if (filters.severity) {
      reports = reports.filter(report => report.severity === filters.severity);
    }
    
    if (filters.dateFrom) {
      reports = reports.filter(report => new Date(report.dateOccurred) >= new Date(filters.dateFrom));
    }
    
    if (filters.dateTo) {
      reports = reports.filter(report => new Date(report.dateOccurred) <= new Date(filters.dateTo));
    }
    
    return reports;
  }
  
  // Create safety protocol
  static async createSafetyProtocol(protocolData) {
    await new Promise(resolve => setTimeout(resolve, 500));
    
    const newProtocol = {
      id: `protocol-${Date.now()}`,
      ...protocolData,
      lastUpdated: new Date().toISOString().split('T')[0]
    };
    
    mockSafetyProtocols.push(newProtocol);
    return newProtocol;
  }
  
  // Update safety protocol
  static async updateSafetyProtocol(id, updateData) {
    await new Promise(resolve => setTimeout(resolve, 500));
    
    const index = mockSafetyProtocols.findIndex(protocol => protocol.id === id);
    if (index !== -1) {
      mockSafetyProtocols[index] = { 
        ...mockSafetyProtocols[index], 
        ...updateData,
        lastUpdated: new Date().toISOString().split('T')[0]
      };
      return mockSafetyProtocols[index];
    }
    
    throw new Error('Safety protocol not found');
  }
  
  // Delete safety protocol
  static async deleteSafetyProtocol(id) {
    await new Promise(resolve => setTimeout(resolve, 300));
    
    const index = mockSafetyProtocols.findIndex(protocol => protocol.id === id);
    if (index !== -1) {
      mockSafetyProtocols.splice(index, 1);
      return true;
    }
    
    throw new Error('Safety protocol not found');
  }
  
  // Create emergency procedure
  static async createEmergencyProcedure(procedureData) {
    await new Promise(resolve => setTimeout(resolve, 500));
    
    const newProcedure = {
      id: `emergency-${Date.now()}`,
      ...procedureData
    };
    
    mockEmergencyProcedures.push(newProcedure);
    return newProcedure;
  }
  
  // Update emergency procedure
  static async updateEmergencyProcedure(id, updateData) {
    await new Promise(resolve => setTimeout(resolve, 500));
    
    const index = mockEmergencyProcedures.findIndex(procedure => procedure.id === id);
    if (index !== -1) {
      mockEmergencyProcedures[index] = { ...mockEmergencyProcedures[index], ...updateData };
      return mockEmergencyProcedures[index];
    }
    
    throw new Error('Emergency procedure not found');
  }
  
  // Delete emergency procedure
  static async deleteEmergencyProcedure(id) {
    await new Promise(resolve => setTimeout(resolve, 300));
    
    const index = mockEmergencyProcedures.findIndex(procedure => procedure.id === id);
    if (index !== -1) {
      mockEmergencyProcedures.splice(index, 1);
      return true;
    }
    
    throw new Error('Emergency procedure not found');
  }

  // Create injury report
  static async createInjuryReport(reportData) {
    await new Promise(resolve => setTimeout(resolve, 500));
    
    const newReport = {
      id: `injury-${Date.now()}`,
      ...reportData,
      dateReported: new Date().toISOString().split('T')[0]
    };
    
    mockInjuryReports.push(newReport);
    return newReport;
  }
  
  // Update injury report
  static async updateInjuryReport(id, updateData) {
    await new Promise(resolve => setTimeout(resolve, 500));
    
    const index = mockInjuryReports.findIndex(report => report.id === id);
    if (index !== -1) {
      mockInjuryReports[index] = { ...mockInjuryReports[index], ...updateData };
      return mockInjuryReports[index];
    }
    
    throw new Error('Injury report not found');
  }
  
  // Get safety resources
  static async getSafetyResources(category = null) {
    await new Promise(resolve => setTimeout(resolve, 300));
    
    if (category) {
      return mockSafetyResources.filter(resource => resource.category === category);
    }
    
    return mockSafetyResources;
  }
  
  // Get safety statistics
  static async getSafetyStatistics() {
    await new Promise(resolve => setTimeout(resolve, 400));
    
    const totalReports = mockInjuryReports.length;
    const severityBreakdown = {
      [INJURY_SEVERITY.MINOR]: mockInjuryReports.filter(r => r.severity === INJURY_SEVERITY.MINOR).length,
      [INJURY_SEVERITY.MODERATE]: mockInjuryReports.filter(r => r.severity === INJURY_SEVERITY.MODERATE).length,
      [INJURY_SEVERITY.SEVERE]: mockInjuryReports.filter(r => r.severity === INJURY_SEVERITY.SEVERE).length,
      [INJURY_SEVERITY.CRITICAL]: mockInjuryReports.filter(r => r.severity === INJURY_SEVERITY.CRITICAL).length
    };
    
    const sportBreakdown = mockInjuryReports.reduce((acc, report) => {
      acc[report.sport] = (acc[report.sport] || 0) + 1;
      return acc;
    }, {});
    
    return {
      totalReports,
      severityBreakdown,
      sportBreakdown,
      protocolsCount: mockSafetyProtocols.length,
      emergencyProceduresCount: mockEmergencyProcedures.length
    };
  }
  
  // Utility functions
  static formatDate(dateString) {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }
  
  static getSeverityColor(severity) {
    const colors = {
      [INJURY_SEVERITY.MINOR]: 'text-green-600 bg-green-100',
      [INJURY_SEVERITY.MODERATE]: 'text-yellow-600 bg-yellow-100',
      [INJURY_SEVERITY.SEVERE]: 'text-orange-600 bg-orange-100',
      [INJURY_SEVERITY.CRITICAL]: 'text-red-600 bg-red-100'
    };
    return colors[severity] || 'text-gray-600 bg-gray-100';
  }
  
  static getPriorityColor(priority) {
    const colors = {
      'low': 'text-green-600 bg-green-100',
      'medium': 'text-yellow-600 bg-yellow-100',
      'high': 'text-orange-600 bg-orange-100',
      'critical': 'text-red-600 bg-red-100'
    };
    return colors[priority] || 'text-gray-600 bg-gray-100';
  }
}

export default SafetyService;
export { mockSafetyProtocols, mockEmergencyProcedures, mockInjuryReports, mockSafetyResources };